import React from 'react';
import { Alert, AlertTitle, Box, Button, Chip, Typography } from '@mui/material';
import { EnterpriseProductionDeployPanel } from './EnterpriseProductionDeployPanel';
import { EnterpriseDeployJob } from './enterpriseDeployShared';
import {
  enterpriseProductionReady,
  productionReadyChipColor,
  productionReadyChipLabel,
  setupProductionGateBlocked,
} from './setupProductionReady';

type Props = {
  deviceId: string;
  deviceIp?: string;
  enterpriseJob: EnterpriseDeployJob | null;
  canReadDeployments: boolean;
  canWriteDeployments: boolean;
  onJobChange: (job: EnterpriseDeployJob | null) => void;
};

export const SetupProductionReadyGate: React.FC<Props> = ({
  deviceId,
  deviceIp,
  enterpriseJob,
  canReadDeployments,
  canWriteDeployments,
  onJobChange,
}) => {
  const productionReady = enterpriseProductionReady(enterpriseJob);
  const productionGateBlocked = setupProductionGateBlocked(enterpriseJob, canReadDeployments);

  if (!canReadDeployments) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          size="small"
          label={productionReadyChipLabel(enterpriseJob)}
          color={productionReadyChipColor(enterpriseJob)}
        />
        {enterpriseJob?.release_id && (
          <Chip size="small" variant="outlined" label={`release ${enterpriseJob.release_id}`} />
        )}
      </Box>

      {productionGateBlocked && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <AlertTitle>Production deploy required</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Configure home is gated until <strong>Production deploy (Enterprise)</strong> acceptance
            passes. Run production deploy from Bring Up or below, then continue staged setup.
          </Typography>
          {canWriteDeployments && (
            <EnterpriseProductionDeployPanel
              deviceId={deviceId}
              device={{ device_id: deviceId, ip_address: deviceIp }}
              variant="compact"
              showPostBringUpCta
              onJobChange={onJobChange}
            />
          )}
          <Button
            size="small"
            sx={{ mt: 1 }}
            variant="outlined"
            href={`/bringup?device=${encodeURIComponent(deviceId)}`}
          >
            Open Bring Up
          </Button>
        </Alert>
      )}

      {productionReady && (
        <Alert severity="success" sx={{ mt: 2 }}>
          Enterprise production deploy acceptance passed. You can continue staged setup for this device.
        </Alert>
      )}
    </>
  );
};

export function isSetupContinueBlocked(
  enterpriseJob: EnterpriseDeployJob | null,
  canReadDeployments: boolean,
): boolean {
  return setupProductionGateBlocked(enterpriseJob, canReadDeployments);
}
